import {
  Background,
  BaseEdge,
  Connection,
  Controls,
  Edge,
  EdgeLabelRenderer,
  EdgeTypes,
  getBezierPath,
  Handle,
  Node,
  NodeTypes,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiClock, FiExternalLink, FiFlag, FiGitBranch, FiSearch, FiTrash2, FiUser, FiX } from 'react-icons/fi';
import * as sched from 'sched';

// Initialize ELK layout engine
const elk = new ELK();

// ELK layout options for workflow graphs
const elkOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.spacing.nodeNode': '80',
  'elk.layered.spacing.nodeNodeBetweenLayers': '120',
  'elk.spacing.edgeNode': '40',
  'elk.spacing.edgeEdge': '20'
};

export interface WorkflowNodeData {
  needsApproval: boolean;
  state: sched.WorkflowStepUserDisplayState;
  selectedBranches?: string[];
}

export type WorkflowGraphNodeData = {
  step: sched.WorkflowTemplateStep;
  workflowData?: WorkflowNodeData;
  branchHandlePositions?: Record<string, number>;
  displayContext: NodeDisplayContext;
  /** 0 = not on critical path, 1/3, 2/3, or 1 = on 1, 2, or 3 of the estimate paths. */
  criticalPathIntensity?: number;
  /** Minimum slack across the three estimates (days), shown as tooltip. */
  slackDays?: { low: number; mode: number; high: number } | null;
}

// Custom edge component with delete button (for template editing)
const DeletableEdge = ({ id, sourceX, sourceY, targetX, targetY, data }: any) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const cpIntensity: number = data?.criticalPathIntensity ?? 0;
  const edgeStroke = cpIntensity >= 1
    ? '#ef4444'
    : cpIntensity >= 2 / 3
      ? 'rgba(239,68,68,0.7)'
      : cpIntensity > 0
        ? 'rgba(239,68,68,0.5)'
        : '#64748b';
  const edgeWidth = cpIntensity > 0 ? 3 : 2;

  return (
    <>
      {/* Glow layer for critical path edges */}
      {cpIntensity > 0 && (
        <BaseEdge
          path={edgePath}
          style={{ stroke: edgeStroke, strokeWidth: edgeWidth + 4, opacity: 0.25, filter: 'blur(3px)' }}
        />
      )}
      <BaseEdge
        path={edgePath}
        markerEnd="url(#arrow)"
        style={{ stroke: edgeStroke, strokeWidth: edgeWidth }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <button
            onClick={() => data?.onDelete?.(id, {
              linkId: data?.linkId,
              sourceStepId: data?.sourceStepId,
              targetStepId: data?.targetStepId,
            })}
            className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity shadow-md"
            title="Delete link"
          >
            <FiTrash2 className="w-3 h-3" />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};


interface NodeDisplayContext {
  // Text to display below the node's title
  subtitle: string | null;

  // Classes to apply to the node (e.g. color)
  className?: string;

  // Icon to display on the left side of the node
  Icon?: React.ReactNode;

  // Whether to animate the node's style to draw user attention
  animateHighlight?: boolean;

  // Whether to animate a loading spinner
  animateLoadingSpinner?: boolean;

  // If set, the step has been marked complete at this datetime string
  completedAt?: string;
}

interface AvatarFields {
  name: string;
  url: string | null;
  additionalUsers: number;
}

const getAvatarFieldsForSubject = (subject: sched.WorkflowAuthorizationSubject, userProfiles: sched.UserProfile[]): AvatarFields | null => {
  if (subject.type === "Users" && subject.ids.length > 0) {
    const firstUserId = subject.ids[0];

    const userProfile = userProfiles.find(p => p.id == firstUserId);

    if (userProfile) {
      return {
        name: userProfile.display_name,
        url: userProfile.avatar_url,
        additionalUsers: subject.ids.length - 1
      }
    }
  }

  return null;
}

const getSubtitleForWorkflowStepDisplayState = (state: sched.WorkflowStepUserDisplayState) => {
  switch (state.type) {
    case 'Complete':
      return 'Complete';
    case 'PendingUserAction':
      if (state.includes_viewer) {
        return 'Waiting on You';
      } else {
        return 'Waiting on Others';
      }
    case 'PendingTime':
      return 'Pending Time';
    case 'Blocked':
      return 'Blocked';
    default:
      return 'Unknown';
  }
}

const getSubtitleForWorkflowTemplateStep = (step: sched.WorkflowTemplateStep) => {
  switch (step.completion.type) {
    case 'Manual':
      return 'Task';
    case 'Branch':
      return 'Decision';
    case 'Immediate':
      return 'Checkpoint';
    case 'TimeElapsed':
      return 'Timer';
    case 'TimeReached':
      return 'Timer';
    default:
      // Covers ExternalDependency and any future types
      return 'External';
  }
}

interface DisplayThings {
  // CSS classes to apply to the node's outer container
  className: string;
  // Whether to animate an icon to draw the user's attendion
  animateHighlight: boolean;
  // Whether to animate a little loading icon
  animateLoadingSpinner: boolean;
}

const getDisplayThingsForWorkflowStepDisplayState = (state: sched.WorkflowStepUserDisplayState): DisplayThings => {
  switch (state.type) {
    case 'Complete':
      return { className: 'border-green-500 dark:border-green-400 opacity-50', animateHighlight: false, animateLoadingSpinner: false };
    case 'PendingUserAction':
      if (state.includes_viewer) {
        return { className: 'border-blue-500 dark:border-blue-400', animateHighlight: true, animateLoadingSpinner: false };
      } else {
        return { className: '', animateHighlight: false, animateLoadingSpinner: false };
      }
    case 'PendingTime':
      return { className: '', animateHighlight: false, animateLoadingSpinner: true };
    case 'Blocked':
      return { className: '', animateHighlight: false, animateLoadingSpinner: false };
  }
}

const getIconComponentForWorkflowStep = (step: sched.WorkflowTemplateStep) => {
  switch (step.completion.type) {
    case 'Manual':
      return FiUser;
    case 'Branch':
      return FiGitBranch;
    case 'Immediate':
      return FiFlag;
    case 'TimeElapsed':
      return FiClock;
    case 'TimeReached':
      return FiClock;
    default:
      // Covers ExternalDependency and any future types
      return FiExternalLink;
  }
}

// The node's avatar/icon is the first user who is responsible for it, if any
// If this is a running workflow and the step is complete, then the icon component also contains a little checkmark in the bottom left.
// If the step is the kind that's waiting for time to run, then the icon is an hour glass with a little timer in the bottom left.
// If the step is immediate, then it's a flag (as a checkpoint)
// If the step is blocked, then it's a clock and opacity is reduced
export const getNodeDisplayContext = (userProfiles: sched.UserProfile[], step: sched.WorkflowTemplateStep, workflowData?: WorkflowNodeData): NodeDisplayContext => {
  let subtitle = null;
  let Icon = undefined;
  let animateHighlight = false;
  let className = "";
  let animateLoadingSpinner = false;

  // Gets the user's attention if we're waiting on them
  if (workflowData?.state.type === "PendingUserAction" && workflowData?.state.includes_viewer) {
    animateHighlight = true;
  }

  let avatarFields = null;
  let subtitlePrefix = ""

  if (step.completion.type === "Manual") {
    avatarFields = getAvatarFieldsForSubject(step.completion.by, userProfiles);
    subtitlePrefix = "Pending "
  } else if (step.completion.type === "Branch") {
    avatarFields = getAvatarFieldsForSubject(step.completion.decider, userProfiles);
    subtitlePrefix = "For Decision by "
  } else if (step.completion.type === "Gate") {
    avatarFields = getAvatarFieldsForSubject((step.completion as any).updater, userProfiles);
    subtitlePrefix = "Updated by "
  } else if (step.completion.type === "ExternalEvent") {
    avatarFields = getAvatarFieldsForSubject((step.completion as any).updater, userProfiles);
    subtitlePrefix = "Monitored by "
  }

  if (avatarFields) {
    if (avatarFields.url) {
      Icon = <img src={avatarFields.url} alt={avatarFields.name} className="w-6 h-6 rounded-full" />;
    } else {
      Icon = <FiUser className="w-6 h-6" />;
    }
    subtitle = `${subtitlePrefix}${avatarFields.name}`;
    if (avatarFields.additionalUsers > 0) {
      subtitle += ` +${avatarFields.additionalUsers}`;
    }
  }

  if (subtitle === null) {
    if (workflowData) {
      subtitle = getSubtitleForWorkflowStepDisplayState(workflowData.state);
    } else {
      subtitle = getSubtitleForWorkflowTemplateStep(step);
    }
  }

  if (workflowData) {
    const displayThings = getDisplayThingsForWorkflowStepDisplayState(workflowData.state);
    className = displayThings.className;
    animateHighlight = displayThings.animateHighlight;
    animateLoadingSpinner = displayThings.animateLoadingSpinner;
  }

  if (!Icon) {
    const IconComponent = getIconComponentForWorkflowStep(step);
    Icon = <IconComponent className="w-6 h-6" />;
  }

  // TODO restore some of this
  // } else if (workflowData?.state.type === "PendingTime") {
  //   const time = new Date(workflowData.state.time);
  //   const now = new Date();
  //   const diff = time.getTime() - now.getTime();
  //   const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));
  //   const diffHours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  //   const diffMinutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  //   const diffSeconds = Math.floor((diff % (1000 * 60)) / 1000);

  //   if (diffDays > 0) {
  //     subtitle = `Waiting for ${diffDays} days`;
  //   } else if (diffHours > 0) {
  //     subtitle = `Waiting for ${diffHours} hours`;
  //   } else if (diffMinutes > 0) {
  //     subtitle = `Waiting for ${diffMinutes} minutes`;
  //   } else if (diffSeconds > 0) {
  //     subtitle = `Waiting for ${diffSeconds} seconds`;
  //   } else {
  //     subtitle = "Completing shortly...";
  //   }
  // } else if (workflowData?.state.type === "Complete") {
  //   subtitle = "Complete";
  // } else if (workflowData?.state.type === "Blocked") {
  //   subtitle = "Blocked";
  // }

  return {
    subtitle,
    animateHighlight,
    Icon,
    className,
    animateLoadingSpinner,
  }
}

const PingDot = () => {
  return <span className="relative flex size-3">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75"></span>
    <span className="relative inline-flex size-3 rounded-full bg-sky-500"></span>
  </span>
}

// Type → accent color mapping for the left edge strip
const typeAccent: Record<string, { border: string; bg: string; text: string }> = {
  Manual:   { border: 'border-l-blue-500',   bg: 'bg-blue-100 dark:bg-blue-900/30',     text: 'text-blue-700' },
  Branch:   { border: 'border-l-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
  TimeElapsed:  { border: 'border-l-amber-500',  bg: 'bg-amber-100 dark:bg-amber-900/30',   text: 'text-amber-700 dark:text-amber-300' },
  TimeReached:  { border: 'border-l-amber-500',  bg: 'bg-amber-100 dark:bg-amber-900/30',   text: 'text-amber-700 dark:text-amber-300' },
  Immediate:    { border: 'border-l-gray-400',   bg: 'bg-cm-bg-hover',        text: 'text-cm-text-secondary' },
  Gate:         { border: 'border-l-teal-500',  bg: 'bg-teal-100 dark:bg-teal-900/30',     text: 'text-teal-700 dark:text-teal-300' },
  ExternalEvent: { border: 'border-l-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
  ExternalDependency: { border: 'border-l-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
};

const formatEstimate = (est: { min?: number | null; expected?: number | null; max?: number | null } | null | undefined): string | null => {
  if (!est) return null;
  const parts = [est.min, est.expected, est.max].map(v => v != null ? String(v) : '?');
  if (parts.every(p => p === '?')) return null;
  return parts.join('/');
};

// Custom node component for workflow template steps
const WorkflowTemplateStepNode = ({ data }: { data: WorkflowGraphNodeData }) => {
  const isBranchingStep = data.step.completion?.type === 'Branch';
  const branchOptions = isBranchingStep && data.step.completion?.type === 'Branch'
    ? data.step.completion.options
    : {};
  const branchEntries = Object.entries(branchOptions || {});

  const accent = typeAccent[data.step.completion.type] ?? typeAccent.Immediate;

  // Build compact metadata chips
  const chips: { label: string; className: string }[] = [];
  chips.push({ label: data.displayContext.subtitle ?? data.step.completion.type, className: `${accent.bg} ${accent.text}` });

  const completion = data.step.completion;
  if (completion.type === 'Manual' && completion.work_estimate) {
    const est = formatEstimate(completion.work_estimate);
    if (est) chips.push({ label: est, className: 'bg-cm-bg-hover text-cm-text-secondary' });
  }
  if (completion.type === 'Branch' && completion.duration_estimate) {
    const est = formatEstimate(completion.duration_estimate);
    if (est) chips.push({ label: est, className: 'bg-cm-bg-hover text-cm-text-secondary' });
  }
  if (data.step.condition?.type === 'BoolParameter') {
    const label = `${data.step.condition.negate ? '!' : ''}${data.step.condition.key || '?'}`;
    chips.push({ label: `if ${label}`, className: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300' });
  }

  // Critical path highlight: ring intensity scales with how many paths the node is on
  const cpIntensity = data.criticalPathIntensity ?? 0;
  const cpRingClass = cpIntensity >= 1
    ? 'ring-[3px] ring-red-500 dark:ring-red-400'
    : cpIntensity >= 2 / 3
      ? 'ring-[3px] ring-red-400 dark:ring-red-400/80'
      : cpIntensity > 0
        ? 'ring-2 ring-red-300 dark:ring-red-400/60'
        : '';
  const cpGlow = cpIntensity >= 1
    ? '0 0 12px rgba(239,68,68,0.6), 0 0 4px rgba(239,68,68,0.3)'
    : cpIntensity >= 2 / 3
      ? '0 0 10px rgba(239,68,68,0.4), 0 0 3px rgba(239,68,68,0.2)'
      : cpIntensity > 0
        ? '0 0 8px rgba(239,68,68,0.25)'
        : undefined;

  // Build tooltip with slack info
  let titleText = data.step.title;
  if (data.slackDays) {
    const { low, mode, high } = data.slackDays;
    titleText += `\nSlack: ${low.toFixed(1)}d (low) / ${mode.toFixed(1)}d (mode) / ${high.toFixed(1)}d (high)`;
  }

  const isCompleted = !!data.displayContext.completedAt;
  const bgClass = isCompleted
    ? 'bg-green-50 dark:bg-green-900/20'
    : 'bg-cm-bg-surface';
  const borderClass = isCompleted
    ? 'border-green-300 dark:border-green-700'
    : 'border-cm-border-subtle';

  return (
    <div
      className={`rounded-md ${bgClass} border ${borderClass} border-l-[4px] ${accent.border} relative shadow-sm hover:shadow-md transition-shadow ${data.displayContext.className} ${cpRingClass}`}
      style={{ width: 172, minHeight: 62, boxShadow: cpGlow }}
      title={titleText}
    >
      {/* Input handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-2.5 h-2.5 bg-gray-400 border-2 border-white"
      />

      {isCompleted && (
        <div className="absolute top-1 right-1" title={`Completed ${new Date(data.displayContext.completedAt!).toLocaleDateString()}`}>
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      {!isCompleted && data.displayContext.animateHighlight && (
        <div className="absolute top-1 right-1">
          <PingDot />
        </div>
      )}
      {!isCompleted && data.displayContext.animateLoadingSpinner && (
        <div className="absolute top-1.5 right-1.5">
          <div className="animate-spin rounded-full border-b-2 border-blue-500 h-3 w-3"></div>
        </div>
      )}

      <div className="flex flex-col justify-center h-full px-2.5 py-1.5 gap-0.5">
        <div className="text-[11px] font-semibold text-cm-text-primary leading-snug line-clamp-2">
          {data.step.title}
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {chips.map((chip, i) => (
            <span key={i} className={`inline-block px-1.5 py-px rounded text-[9px] font-medium leading-tight ${chip.className}`}>
              {chip.label}
            </span>
          ))}
          {/* Show selected branches for workflow instances */}
          {data.workflowData && isBranchingStep && data.workflowData.selectedBranches && (
            <span className="inline-block px-1.5 py-px rounded text-[9px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700">
              {data.workflowData.selectedBranches.map((branchId: string) => branchOptions[branchId] || branchId).join(', ')}
            </span>
          )}
        </div>
      </div>

      {/* Output handles */}
      {isBranchingStep && branchEntries.length > 0 ? (
        branchEntries.map(([branchId, branchLabel], index) => {
          const totalBranches = branchEntries.length;
          const handlePosition = data.branchHandlePositions?.[branchId] ?? (
            totalBranches === 1
              ? 0.5
              : (index + 1) / (totalBranches + 1)
          );

          return (
            <React.Fragment key={branchId}>
              <Handle
                type="source"
                position={Position.Bottom}
                id={branchId}
                style={{
                  left: `${handlePosition * 100}%`,
                  transform: 'translateX(-50%)'
                }}
                className="w-2.5 h-2.5 bg-purple-500 border-2 border-white"
              />
              <div
                className="absolute text-[9px] text-purple-600 dark:text-purple-400 font-medium"
                style={{
                  bottom: '-18px',
                  left: `${handlePosition * 100}%`,
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                  maxWidth: '80px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {branchLabel}
              </div>
            </React.Fragment>
          );
        })
      ) : (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2.5 h-2.5 bg-gray-400 border-2 border-white"
        />
      )}
    </div>
  );
};

const nodeTypes: NodeTypes = {
  workflowStep: WorkflowTemplateStepNode,
};

const edgeTypes: EdgeTypes = {
  deletable: DeletableEdge,
};

// ─── Graph Search Panel ─────────────────────────────────────────────────────

const GraphSearchPanel: React.FC<{ nodes: Node<WorkflowGraphNodeData>[] }> = ({ nodes }) => {
  const { setCenter } = useReactFlow();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [currentIdx, setCurrentIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = React.useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return nodes.filter(n => n.data.step.title.toLowerCase().includes(q));
  }, [query, nodes]);

  // Reset index when matches change
  useEffect(() => {
    setCurrentIdx(0);
  }, [matches.length, query]);

  // Focus input when opened
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Keyboard shortcut: Ctrl/Cmd+F to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const goTo = useCallback((idx: number) => {
    const node = matches[idx];
    if (!node) return;
    setCurrentIdx(idx);
    const x = node.position.x + (node.measured?.width ?? 172) / 2;
    const y = node.position.y + (node.measured?.height ?? 62) / 2;
    setCenter(x, y, { zoom: 1.5, duration: 300 });
  }, [matches, setCenter]);

  const next = () => { if (matches.length) goTo((currentIdx + 1) % matches.length); };
  const prev = () => { if (matches.length) goTo((currentIdx - 1 + matches.length) % matches.length); };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="absolute top-3 right-3 z-20 p-2 rounded-lg bg-cm-bg-surface border border-cm-border-subtle shadow-sm hover:bg-cm-bg-elevated text-cm-text-muted"
        title="Search nodes (Ctrl+F)"
      >
        <FiSearch className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-cm-bg-surface border border-cm-border-subtle rounded-lg shadow-md px-2 py-1.5">
      <FiSearch className="w-3.5 h-3.5 text-cm-text-muted flex-shrink-0" />
      <input
        ref={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.shiftKey ? prev() : next(); }
          if (e.key === 'Escape') { setOpen(false); setQuery(''); }
        }}
        placeholder="Search steps..."
        className="w-44 bg-transparent border-none outline-none text-sm text-cm-text-primary placeholder-gray-400 px-1 py-0 focus:ring-0"
      />
      {query && (
        <span className="text-xs text-cm-text-muted tabular-nums flex-shrink-0">
          {matches.length > 0 ? `${currentIdx + 1}/${matches.length}` : '0/0'}
        </span>
      )}
      <button onClick={prev} disabled={!matches.length} className="p-0.5 text-cm-text-muted hover:text-cm-text-primary disabled:opacity-30" title="Previous (Shift+Enter)">
        <FiChevronUp className="w-3.5 h-3.5" />
      </button>
      <button onClick={next} disabled={!matches.length} className="p-0.5 text-cm-text-muted hover:text-cm-text-primary disabled:opacity-30" title="Next (Enter)">
        <FiChevronDown className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => { setOpen(false); setQuery(''); }} className="p-0.5 text-cm-text-muted hover:text-cm-text-primary" title="Close (Esc)">
        <FiX className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export interface WorkflowGraphProps {
  nodes: Node<WorkflowGraphNodeData>[];
  edges: Edge[];
  onNodesChange?: (changes: any) => void;
  onEdgesChange?: (changes: any) => void;
  onConnect?: (connection: Connection) => void;
  onNodeClick?: (event: React.MouseEvent, node: Node<WorkflowGraphNodeData>) => void;
  nodesDraggable?: boolean;
  nodesConnectable?: boolean;
  elementsSelectable?: boolean;
  /** If true, run ELK layout whenever nodes/edges change. */
  enableAutoLayout?: boolean;
  className?: string;
  /** Imperative layout trigger — increment to force a re-layout. */
  layoutGeneration?: number;
}

export const WorkflowGraph: React.FC<WorkflowGraphProps> = ({
  nodes: initialNodes,
  edges: initialEdges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  nodesDraggable = true,
  nodesConnectable = true,
  elementsSelectable = true,
  enableAutoLayout = false,
  className = '',
  layoutGeneration = 0,
}) => {
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState<Node<WorkflowGraphNodeData>>(initialNodes);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState<Edge>(initialEdges);
  const [layoutLoading, setLayoutLoading] = useState(false);

  // Function to calculate optimal branch handle positions based on child node positions
  const calculateBranchHandlePositions = useCallback((
    parentNode: Node<WorkflowGraphNodeData>,
    childNodes: Node<WorkflowGraphNodeData>[],
    edges: Edge[]
  ) => {
    const completion = parentNode.data.step.completion;
    const isBranchingStep = completion?.type === 'Branch';
    if (!isBranchingStep || !completion || completion.type !== 'Branch') return {};

    const branchOptions = completion.options;
    const branchEntries = Object.entries(branchOptions || {});

    if (branchEntries.length <= 1) return {};

    // Find child nodes connected to this parent through specific branch handles
    const connectedChildren = edges
      .filter(edge => edge.source === parentNode.id && edge.sourceHandle)
      .map(edge => {
        const childNode = childNodes.find(n => n.id === edge.target);
        return {
          branchId: edge.sourceHandle!,
          childNode,
          childX: childNode?.position.x || 0
        };
      })
      .filter(item => item.childNode)
      .sort((a, b) => a.childX - b.childX); // Sort by X position (left to right)

    // Calculate handle positions based on child positions
    const handlePositions: Record<string, number> = {};
    const parentX = parentNode.position.x;
    const parentWidth = 200; // Node width

    connectedChildren.forEach((item, index) => {
      const childCenterX = item.childX + 100; // Child center (assuming 200px width)
      const relativeToParen = childCenterX - parentX;
      const normalizedPosition = Math.max(0.15, Math.min(0.85, relativeToParen / parentWidth));
      handlePositions[item.branchId] = normalizedPosition;
    });

    // For branches without connected children, distribute remaining space
    const unconnectedBranches = branchEntries
      .map(([branchId]) => branchId)
      .filter(branchId => !handlePositions[branchId]);

    if (unconnectedBranches.length > 0) {
      const usedPositions = Object.values(handlePositions);
      const availableSpace = [0.2, 0.4, 0.6, 0.8].filter(pos =>
        !usedPositions.some(used => Math.abs(used - pos) < 0.15)
      );

      unconnectedBranches.forEach((branchId, index) => {
        if (availableSpace[index] !== undefined) {
          handlePositions[branchId] = availableSpace[index];
        } else {
          // Fallback to even distribution
          handlePositions[branchId] = (index + 1) / (unconnectedBranches.length + 1);
        }
      });
    }

    return handlePositions;
  }, []);

  // Function to apply ELK layout to nodes
  const getLayoutedElements = useCallback(async (nodes: Node<WorkflowGraphNodeData>[], edges: Edge[]) => {
    if (!nodes.length) return { nodes, edges };

    setLayoutLoading(true);

    // Filter out edges referencing nodes that no longer exist (e.g. after
    // a step was deleted or detached without cleaning up all links).
    const nodeIds = new Set(nodes.map(n => n.id));
    const validEdges = edges.filter(edge => {
      const srcOk = nodeIds.has(edge.source);
      const tgtOk = nodeIds.has(edge.target);
      if (!srcOk || !tgtOk) {
        console.warn(`[WorkflowGraph] Dropping dangling edge ${edge.id}: source=${edge.source}(${srcOk ? 'ok' : 'missing'}) target=${edge.target}(${tgtOk ? 'ok' : 'missing'})`);
        return false;
      }
      return true;
    });

    // Convert the nodes and edges to the format ELK expects
    const elkGraph = {
      id: 'root',
      layoutOptions: elkOptions,
      children: nodes.map(node => ({
        id: node.id,
        width: 172,
        height: 62
      })),
      edges: validEdges.map(edge => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target]
      }))
    };

    try {
      // Calculate layout with ELK
      const layoutedGraph = await elk.layout(elkGraph);

      // Apply the calculated layout to the nodes
      const layoutedNodes = nodes.map(node => {
        const elkNode = layoutedGraph.children?.find(n => n.id === node.id);
        if (elkNode && elkNode.x !== undefined && elkNode.y !== undefined) {
          node.position = {
            x: elkNode.x,
            y: elkNode.y
          };
        }
        return node;
      });

      // Calculate optimal branch handle positions based on layout
      const nodesWithHandlePositions = layoutedNodes.map(node => {
        const handlePositions = calculateBranchHandlePositions(node, layoutedNodes, edges);
        if (Object.keys(handlePositions).length > 0) {
          return {
            ...node,
            data: {
              ...node.data,
              branchHandlePositions: handlePositions
            }
          };
        }
        return node;
      });

      setLayoutLoading(false);
      return { nodes: nodesWithHandlePositions, edges };
    } catch (error) {
      console.error('Error calculating layout:', error);
      setLayoutLoading(false);
      return { nodes, edges };
    }
  }, [calculateBranchHandlePositions]);

  // ReactFlow instance for programmatic fitView after layout
  const reactFlowInstance = React.useRef<any>(null);

  // Track whether we've done the initial layout
  const hasLayouted = React.useRef(false);

  // When enableAutoLayout is on, re-layout on every data change (instance viewer).
  // Otherwise, update node data in-place preserving user-dragged positions.
  React.useEffect(() => {
    if (enableAutoLayout) {
      const run = async () => {
        const { nodes: ln, edges: le } = await getLayoutedElements(initialNodes, initialEdges);
        setNodes(ln);
        setEdges(le);
        // Wait a frame for React to render, then fit the view
        requestAnimationFrame(() => reactFlowInstance.current?.fitView({ padding: 0.1 }));
      };
      run();
    } else {
      setNodes(prev => {
        const positionMap = new Map(prev.map(n => [n.id, n.position]));
        return initialNodes.map(node => ({
          ...node,
          position: positionMap.get(node.id) ?? node.position,
        }));
      });
      setEdges(initialEdges);
    }
  }, [initialNodes, initialEdges, enableAutoLayout]);

  // Run ELK layout on first mount (once nodes are available) or when layoutGeneration is bumped
  React.useEffect(() => {
    if (enableAutoLayout) return;
    // Skip if there are no nodes yet (template still loading)
    if (initialNodes.length === 0) return;
    if (hasLayouted.current && layoutGeneration === 0) return;
    hasLayouted.current = true;
    const run = async () => {
      const { nodes: ln, edges: le } = await getLayoutedElements(initialNodes, initialEdges);
      setNodes(ln);
      setEdges(le);
      requestAnimationFrame(() => reactFlowInstance.current?.fitView({ padding: 0.1 }));
    };
    run();
  }, [layoutGeneration, initialNodes, initialEdges]);

  const handleNodesChange = onNodesChange || onNodesChangeInternal;
  const handleEdgesChange = onEdgesChange || onEdgesChangeInternal;

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Layout Loading Indicator */}
      {layoutLoading && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex items-center gap-2 bg-cm-bg-surface px-3 py-2 rounded-lg shadow-md border border-cm-border-subtle">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-cm-text-primary">Calculating layout...</span>
          </div>
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={nodesDraggable}
        nodesConnectable={nodesConnectable}
        elementsSelectable={elementsSelectable}
        onNodeClick={onNodeClick}
        onInit={(instance) => { reactFlowInstance.current = instance; }}
        minZoom={0.2}
        fitView
      >
        <Controls
          style={{
            backgroundColor: 'var(--tw-bg-opacity, 1) rgb(31 41 55 / var(--tw-bg-opacity))',
            borderColor: 'var(--tw-border-opacity, 1) rgb(75 85 99 / var(--tw-border-opacity))',
          }}
          className="[&>button]:!bg-cm-bg-surface [&>button]:!text-white [&>button]:!border-cm-border-mid [&>button:hover]:!bg-cm-bg-elevated dark:[&>button]:!bg-cm-bg-surface dark:[&>button]:!text-white dark:[&>button]:!border-cm-border-mid dark:[&>button:hover]:!bg-cm-bg-elevated"
        />
        <Background />
        <GraphSearchPanel nodes={nodes} />
      </ReactFlow>
    </div>
  );
};

export default WorkflowGraph;
